import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
	GetInsuranceProperties,
	InsuranceProperties,
} from '../../API/ClientAPIHelpers/insuranceAuthorizationProperties';
import API from '../../API/Main/API';
import { InsuranceDetailProperties } from '../../API/ClientAPIHelpers/insuranceProperties';

interface ClientShort {
	fullName: string;

	id: string;
}

interface UserShort {
	fullName: string;

	id: string;
}

interface Location {
	id: string;
	name: string;
}

interface InsuranceState {
	clients: ClientShort[];
	currentInsurance: InsuranceDetailProperties | null;
	error: string | null;

	insurances: InsuranceProperties[];
	insurancesAdmin: InsuranceDetailProperties[];
	loading: boolean;
	loadingAdmin: boolean;
	loadingDetails: boolean;
	locations: Location[];
	users: UserShort[];
}

const initialState: InsuranceState = {
	insurances: [],
	insurancesAdmin: [],
	currentInsurance: null,
	clients: [],
	users: [],
	locations: [],
	loading: false,
	loadingAdmin: false,
	loadingDetails: false,
	error: null,
};

export const getInsurances = createAsyncThunk('/client/insurances', async (data: GetInsuranceProperties, thunkAPI) => {
	const response = await API.ClientAPI.Insurances.getInsurances(data);

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error);
	}

	return response as InsuranceProperties[];
});

export const getInsurancesForAdmin = createAsyncThunk(
	'insurance/getInsurancesForAdmin',
	async (data: GetInsuranceProperties, thunkAPI) => {
		const response = await API.ClientAPI.Insurances.getInsurancesForAdmin(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

export const getInsurance = createAsyncThunk('insurance/getInsurance', async (id: string, thunkAPI) => {
	const response = await API.ClientAPI.Insurances.getInsuranceById(id);

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error);
	}

	return response;
});

export const getInsuranceClients = createAsyncThunk('insurance/getInsuranceClients', async (id: string, thunkAPI) => {
	const response = await API.ClientAPI.Insurances.getInsuranceClients(id);

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error);
	}

	return response;
});

export const getInsuranceUsers = createAsyncThunk('insurance/getInsuranceUsers', async (id: string, thunkAPI) => {
	const response = await API.ClientAPI.Insurances.getInsuranceUsers(id);

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error);
	}

	return response;
});

export const getInsuranceLocations = createAsyncThunk(
	'insurance/getInsuranceLocations',
	async (id: string, thunkAPI) => {
		const response = await API.ClientAPI.Insurances.getInsuranceLocations(id);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

const insuranceSlice = createSlice({
	name: 'insurance',
	initialState,
	reducers: {
		clearInsuranceDetails: (state) => {
			state.currentInsurance = null;
			state.clients = [];
			state.users = [];
			state.locations = [];
		},
	},
	extraReducers: (builder) => {
		builder
			// getInsurances
			.addCase(getInsurances.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(getInsurances.fulfilled, (state, action) => {
				state.loading = false;
				state.insurances = action.payload;
			})
			.addCase(getInsurances.rejected, (state, action) => {
				state.loading = false;
				state.error = action.error.message || 'Failed to fetch insurances';
			})
			// getInsurancesForAdmin
			.addCase(getInsurancesForAdmin.pending, (state) => {
				state.loadingAdmin = true;
				state.error = null;
			})
			.addCase(getInsurancesForAdmin.fulfilled, (state, action) => {
				state.loadingAdmin = false;
				state.insurancesAdmin = action.payload;
			})
			.addCase(getInsurancesForAdmin.rejected, (state, action) => {
				state.loadingAdmin = false;
				state.error = action.error.message || 'Failed to fetch insurances';
			})
			// getInsurance
			.addCase(getInsurance.pending, (state) => {
				state.loadingDetails = true;
				state.error = null;
			})
			.addCase(getInsurance.fulfilled, (state, action) => {
				state.loadingDetails = false;
				state.currentInsurance = action.payload;
			})
			.addCase(getInsurance.rejected, (state, action) => {
				state.loadingDetails = false;
				state.error = action.error.message || 'Failed to fetch insurance';
			})
			// getInsuranceClients
			.addCase(getInsuranceClients.pending, (state) => {
				state.loadingDetails = true;
			})
			.addCase(getInsuranceClients.fulfilled, (state, action) => {
				state.loadingDetails = false;
				state.clients = action.payload;
			})
			.addCase(getInsuranceClients.rejected, (state, action) => {
				state.loadingDetails = false;
				state.error = action.error.message || 'Failed to fetch clients';
			})
			// getInsuranceUsers
			.addCase(getInsuranceUsers.pending, (state) => {
				state.loadingDetails = true;
			})
			.addCase(getInsuranceUsers.fulfilled, (state, action) => {
				state.loadingDetails = false;
				state.users = action.payload;
			})
			.addCase(getInsuranceUsers.rejected, (state, action) => {
				state.loadingDetails = false;
				state.error = action.error.message || 'Failed to fetch users';
			})
			// getInsuranceLocations
			.addCase(getInsuranceLocations.pending, (state) => {
				state.loadingDetails = true;
			})
			.addCase(getInsuranceLocations.fulfilled, (state, action) => {
				state.loadingDetails = false;
				state.locations = action.payload;
			})
			.addCase(getInsuranceLocations.rejected, (state, action) => {
				state.loadingDetails = false;
				state.error = action.error.message || 'Failed to fetch locations';
			});
	},
});

export const { clearInsuranceDetails } = insuranceSlice.actions;
export default insuranceSlice.reducer;
